<script setup lang="ts">
import { ElTableColumn } from 'element-plus'
import { computed } from 'vue'

import { Abbrev } from '@/shared/ui/Abbrev'
import { Inline } from '@/shared/ui/Inline'
import { Tooltip } from '@/shared/ui/Tooltip'

const props = defineProps<{
  prop?: string
  label?: string
  width?: string | number
  align?: 'left' | 'center' | 'right'
  /**
   * What the column means, when the header alone does not say it.
   *
   * Pass it rather than assembling a tooltip in `#header`: the hint hangs on the label
   * itself instead of on a `?` beside it, because these are the narrow columns — counts and
   * money — and a separate icon costs the width they have least of, pushing the header onto
   * a second line.
   *
   * A `#header` slot still wins, for a column that has to build its own.
   */
  hint?: string
  /**
   * The row's action column — its alignment, its fixed edge and the layout of the controls
   * inside the cell, all at once, so that a table with one button and a table with a button
   * plus a menu line up the same. `align` and `fixed` can still be overridden.
   *
   * The default width fits one `TableActions` dropdown or one small button; pass `width`
   * only when the cell holds more than that.
   */
  variant?: 'actions'
}>()

/** Fits the "Actions ▾" trigger of `TableActions` plus the cell's own padding. */
const ACTIONS_COLUMN_WIDTH = 120

const isActions = computed(() => props.variant === 'actions')
</script>

<template>
  <ElTableColumn
    :prop="prop"
    :label="label"
    :width="width ?? (isActions ? ACTIONS_COLUMN_WIDTH : undefined)"
    :align="align ?? (isActions ? 'center' : undefined)"
    :fixed="isActions ? 'right' : undefined"
    v-bind="$attrs"
  >
    <template v-if="$slots.header" #header="scope">
      <slot name="header" v-bind="scope" />
    </template>
    <template v-else-if="hint" #header>
      <Tooltip placement="top" :content="hint">
        <Abbrev>{{ label }}</Abbrev>
      </Tooltip>
    </template>
    <template v-if="$slots.default" #default="scope">
      <Inline v-if="isActions" :gap="6" align="center" justify="center" wrap>
        <slot v-bind="scope" />
      </Inline>
      <slot v-else v-bind="scope" />
    </template>
  </ElTableColumn>
</template>
